import { Link } from 'react-router-dom';

type BreakdownSegment = {
  label: string;
  count: number;
  color: 'ok' | 'warning' | 'expired';
};

export function KpiBreakdownCard({
  label,
  icon,
  segments,
}: {
  label: string;
  icon: string;
  segments: BreakdownSegment[];
}) {
  const total = segments.reduce((s, seg) => s + seg.count, 0);
  const pct = (count: number) => (total > 0 ? (count / total) * 100 : 0);

  const colorMap = {
    ok: { bg: 'bg-green-100 text-green-700', bar: 'bg-green-500' },
    warning: { bg: 'bg-amber-100 text-amber-700', bar: 'bg-amber-400' },
    expired: { bg: 'bg-red-100 text-red-700', bar: 'bg-red-500' },
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
          <i className={`${icon} text-lg text-foreground-700`}></i>
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-foreground-900 leading-tight">{total}</p>
          <p className="text-xs text-foreground-600">{label}</p>
        </div>
      </div>

      {/* Segmented bar */}
      {total > 0 && (
        <div className="flex w-full h-2 rounded-full overflow-hidden bg-background-200/60">
          {segments.map((seg) => (
            <div
              key={seg.color}
              className={`${colorMap[seg.color].bar} h-full transition-all`}
              style={{ width: `${pct(seg.count)}%`, minWidth: seg.count > 0 ? '4px' : '0px' }}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {segments.map((seg) => (
          <div key={seg.color} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${colorMap[seg.color].bar}`}></div>
            <span className="text-foreground-600">{seg.label}:</span>
            <span className="font-semibold text-foreground-800">{seg.count}</span>
            <span className="text-foreground-400">({pct(seg.count).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== COMPONENTS ==========
export function StatCard({
  label,
  value,
  suffix,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  icon: string;
  color: 'primary' | 'accent' | 'secondary';
}) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    accent: 'bg-accent-50 text-accent-600',
    secondary: 'bg-secondary-50 text-secondary-600',
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center shrink-0`}
      >
        <i className={`${icon} text-xl`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-foreground-900 leading-tight">
          {value}
          {suffix && (
            <span className="text-sm font-normal text-foreground-500 ml-0.5">{suffix}</span>
          )}
        </p>
        <p className="text-xs text-foreground-600 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export function AlertCard({
  type,
  message,
  condo,
  icon,
}: {
  type: string;
  message: string;
  condo: string;
  icon: string;
}) {
  const isWarning = type === 'warning';
  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border ${
        isWarning
          ? 'bg-accent-50/50 border-accent-200/50'
          : 'bg-primary-50/50 border-primary-200/50'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
          isWarning ? 'bg-accent-100 text-accent-600' : 'bg-primary-100 text-primary-600'
        }`}
      >
        <i className={`${icon} text-sm`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground-800">{message}</p>
        <p className="text-xs text-foreground-500 mt-0.5">{condo}</p>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-8">
      <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{title}</h1>
      <p className="text-sm text-foreground-600 mt-1">{subtitle}</p>
    </div>
  );
}
